import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Button } from '@resiliatech/birdsong'
import { useLocation } from 'react-router'
import GET_TEMPLATE_LIBRARY from 'Apollo/Queries/GetTemplateLibrary'
import { USER_SURVEY } from 'Apollo/Queries/UserSurvey'
import CREATE_MANY_NONPROFIT_ORG_USERS from 'Apollo/Mutations/CreateManyNonprofitOrgUsers'
import CREATE_TEMPLATE_GOALS from 'Apollo/Mutations/CreateTemplateGoals'
import { UPSERT_USER_SURVEY } from 'Apollo/Mutations/UpsertUserSurvey'
import GET_ACADEMY_RESOURCES from 'ApolloAcademy/Queries/GetAcademyResources'
import SAVE_ACADEMY_RESOURCE_FOR_USER from 'ApolloAcademy/Mutations/SaveAcademyResourceForUser'
import { Priorities } from 'Components/SharedComponents/Surveys/types'
import { ResiliaIconWhite } from 'Components/Views/AppBar/AppLogoBlue'
import { useAppState } from 'Context/AppContext/AppContext'
import { useHideNavigation } from 'Hooks/useHideNavigation'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import {
  FunderOrgToUserRole,
  Goal,
  NonprofitOrgInviteType,
  SurveyType,
  UserSurveyPrimaryFunction,
  UserSurveyRole,
  useUserSurveysQuery,
} from 'Generated/graphql-types'
import { Resource, ResourceSortOptions } from 'Generated/academy-graphql-types'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { snakeCaseToSpaces } from 'Utils/formatters'
import {
  BackButton,
  FullWindowContainer,
  Header,
  SurveyContainer,
  WizardContainer,
  WizardSteps,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { Close, SignOut } from './NeedsAssessment.styled'
import {
  initialOrgLeaderInvite,
  initialPriorities,
  NeedsAssessmentWizardStep,
} from '../constants'
import { ACADEMY_CONTEXT } from '../../Academy/_common/utils'
import { HandleActionArgs, ToggleSaveOrClearOperation } from '../types'
import { createWizardConfig } from '../config'
import { createTemplateLibraryIdMap } from '../utils'
import { Loading } from '../../Academy/_common/Loading'
import { handleToggleGoals } from '../../../SharedComponents/Surveys/utils'

const useRedirects = () => {
  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId },
    userConfig: { id: userId },
  } = useAppState()

  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const hasSurveyTriggerParam = !!params.get('newSurvey')

  const redirectHome = useCallback(() => {
    window.location.href = '/home'
  }, [])

  const { data: { userSurveys } = {}, loading: surveysLoading } =
    useUserSurveysQuery({
      fetchPolicy: 'network-only',
      variables: { nonprofitOrgId, type: SurveyType.NeedsAssessment },
      skip: !userId,
    })

  useEffect(() => {
    const hasCompletedUserSurvey = (userSurveys || []).some(
      (s) => s.isCompleted
    )
    const hasIncompleteUserSurvey = (userSurveys || []).some(
      (s) => !s.isCompleted
    )

    if (
      !surveysLoading &&
      !hasSurveyTriggerParam &&
      hasCompletedUserSurvey &&
      !hasIncompleteUserSurvey
    ) {
      redirectHome()
    }
  }, [hasSurveyTriggerParam, redirectHome, surveysLoading, userSurveys])
}

export const NeedsAssessment = () => {
  const { logout } = useAuth0()
  const { popToast } = useSnackbar()
  const {
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      loading: nonprofitOrgConfigLoading,
    },
    userConfig: { id: userId },
  } = useAppState()

  const [baselineConfidence, setBaselineConfidence] = useState<string>()
  const [baselineSupportNeeded, setBaselineSupportNeeded] = useState<string>()
  const [currentSessionSurveyId, setCurrentSessionSurveyId] = useState()
  const [shouldLoadSavedProgress, setShouldLoadSavedProgress] = useState(true)
  const [currentStep, setCurrentStep] = useState(
    NeedsAssessmentWizardStep.GET_STARTED
  )
  const [loading, setLoading] = useState(false)
  const [orgLeaderInvite, setOrgLeaderInvite] = useState(initialOrgLeaderInvite)
  const [priorities, setPriorities] = useState(initialPriorities as Priorities)
  const [userPrimaryFunction, setUserPrimaryFunction] =
    useState<UserSurveyPrimaryFunction>()
  const [resourcesToBookmark, setResourcesToBookmark] = useState<string[]>([])
  const [userRole, setUserRole] = useState<UserSurveyRole>()
  const [recommendedResources, setRecommendedResources] = useState<
    Resource[] | undefined
  >([])
  const [userJourney, setUserJourney] = useState<NeedsAssessmentWizardStep[]>([
    NeedsAssessmentWizardStep.GET_STARTED,
  ])
  const [templateGoalsToCreate, setTemplateGoalsToCreate] = useState<number[]>(
    []
  )

  useHideNavigation()

  // Redirect to home if user is at the Needs Assessment (NA) path, has completed one already,
  // doesn't have any in-progress surveys, and we’re not intentionally creating a new one
  useRedirects()

  const { data: { userSurvey } = {}, loading: surveyLoading } = useQuery(
    USER_SURVEY,
    {
      fetchPolicy: 'network-only',
      variables: { isCompleted: false, nonprofitOrgId },
    }
  )

  const [upsertUserSurvey] = useMutation(UPSERT_USER_SURVEY, {
    variables: {
      userSurveyData: {
        id: userSurvey?.id || currentSessionSurveyId,
        nonprofitOrgId,
        response: {
          baselineConfidence,
          baselineSupportNeeded,
          primaryFunction: userPrimaryFunction,
          orgLeaderInvite,
          priorities,
          recommendedResources,
          resourcesToBookmark,
          templateGoalsToCreate,
          userJourney,
          userRole,
        },
        role: userRole,
        type: SurveyType.NeedsAssessment,
      },
    },
    refetchQueries: [USER_SURVEY],
  })

  const [getAcademyResources] = useLazyQuery(GET_ACADEMY_RESOURCES, {
    ...ACADEMY_CONTEXT,
    fetchPolicy: 'network-only',
  })

  const [saveAcademyResourceForUserMutation] = useMutation(
    SAVE_ACADEMY_RESOURCE_FOR_USER,
    { ...ACADEMY_CONTEXT }
  )

  const [createTemplateGoals] = useMutation(CREATE_TEMPLATE_GOALS, {
    onCompleted: (data) => {
      const goals = data?.createTemplateGoals?.goals
      if (goals.length) {
        goals.forEach((g: Goal) => {
          analytics.trackActionCreator(initiatives.Goal_Auto_Created)({
            goalType: g.type,
            templateLibraryId: g.templateLibraryId,
            goalId: g.id,
            goalCreatedDate: new Date().toISOString().slice(0, 10),
            goalUpdatedDate: new Date().toISOString().slice(0, 10),
            goalTitle: g.title,
          })
        })
      }
    },
  })

  const [createUsers] = useMutation(CREATE_MANY_NONPROFIT_ORG_USERS)

  const { data: { getTemplateLibrary: templateLibrary = [] } = {} } =
    useQuery(GET_TEMPLATE_LIBRARY)

  const templateLibraryMap = useMemo(
    () => createTemplateLibraryIdMap(templateLibrary),
    [templateLibrary]
  )

  const { firstPriority, secondPriority, thirdPriority, missingPriority } =
    priorities

  const redirectHome = useCallback(() => {
    window.location.href = '/home'
  }, [])

  /*
    Handles a case where the user's survey is marked complete in the database but the user is still able to
    edit it using the "back" button. This way, the user can continue to edit the same survey until they exit.
   */
  useEffect(() => {
    if (userSurvey) setCurrentSessionSurveyId(userSurvey.id)
  }, [userSurvey])

  useEffect(() => {
    setLoading(surveyLoading || nonprofitOrgConfigLoading)
  }, [surveyLoading, nonprofitOrgConfigLoading])

  // Load the user's previously saved journey if it exists
  useEffect(() => {
    if (!shouldLoadSavedProgress || !userSurvey) return
    setLoading(true)

    const progress = userSurvey?.response
    const step = progress?.userJourney
      ? (progress.userJourney.at(-1) as NeedsAssessmentWizardStep)
      : undefined
    const confidence = progress?.baselineConfidence
    const supportNeeded = progress?.baselineSupportNeeded
    const journey = progress?.userJourney as NeedsAssessmentWizardStep[]
    const orgLeader = progress?.orgLeaderInvite
    const priority = progress?.priorities as unknown as Priorities
    const primaryFunc = progress?.primaryFunction
    const role = progress?.userRole as unknown as UserSurveyRole
    const bookmarks = progress?.resourcesToBookmark
    const goals = progress?.templateGoalsToCreate
    const resources = progress?.recommendedResources

    if (step) setCurrentStep(step)
    if (confidence) setBaselineConfidence(confidence.toString())
    if (supportNeeded) setBaselineSupportNeeded(supportNeeded.toString())
    if (journey) setUserJourney(journey)
    if (orgLeader) setOrgLeaderInvite(orgLeader)
    if (priority) setPriorities(priority)
    if (primaryFunc) setUserPrimaryFunction(primaryFunc)
    if (role) setUserRole(role)
    if (bookmarks) setResourcesToBookmark(bookmarks)
    if (goals) setTemplateGoalsToCreate(goals)
    if (resources) setRecommendedResources(resources)
    setShouldLoadSavedProgress(false)
    setLoading(false)
  }, [shouldLoadSavedProgress, userSurvey])

  // Update the list pre-checked template goals to create whenever the user selects a priority's nested topic
  useEffect(() => {
    // Map isn't built yet while waiting for async calls
    if (Object.keys(templateLibraryMap).length === 0) return

    const userGoals = [
      ...firstPriority.selectedGoals,
      ...secondPriority.selectedGoals,
      ...thirdPriority.selectedGoals,
    ]

    const templateIds = userGoals.map(
      (goal) => templateLibraryMap[goal.templateLibraryId].templateLibraryId
    )

    setTemplateGoalsToCreate(templateIds)
  }, [
    firstPriority.selectedGoals,
    secondPriority.selectedGoals,
    templateLibraryMap,
    thirdPriority.selectedGoals,
  ])

  const fetchAcademyResources = async () => {
    setLoading(true)

    const tags = [
      firstPriority.category,
      secondPriority.category,
      thirdPriority.category,
      missingPriority.category,
    ]
      .filter((t) => !!t)
      .map((tag) => snakeCaseToSpaces(tag, true))

    let resources: Resource[] = []

    // Fetch resources based on user selections
    if (tags.length) {
      const resourcesResponse = await getAcademyResources({
        variables: { limit: 3, tags, sort: ResourceSortOptions.Random },
      })

      resources = resourcesResponse?.data?.resources?.nodes || []
    }

    // Fetch a default resource if no resources were found based on user selections
    if (!resources.length) {
      const fallbackResourcesResponse = await getAcademyResources({
        variables: { query: 'Nonprofit 101: Welcome to Resilia!' },
      })

      resources = fallbackResourcesResponse?.data?.resources?.nodes || []
    }

    // Otherwise, set as undefined to let consumers know we have an issue reaching Academy
    if (!resources.length) {
      setRecommendedResources(undefined)
    } else {
      setRecommendedResources(resources)
      setResourcesToBookmark(resources.map((resource) => resource.id))
    }

    setLoading(false)
  }

  const handleAnyAction = ({
    nextStep,
    callback,
    saveUserState,
  }: HandleActionArgs) => {
    // Wait until the user has selected a role (required) before saving the survey
    if (userRole && userPrimaryFunction && saveUserState) {
      upsertUserSurvey()

      // Prevent future renders from loading the saved progress as user progresses through wizard
      if (shouldLoadSavedProgress) setShouldLoadSavedProgress(false)
    }
    if (callback) callback()
    if (nextStep && wizard[nextStep]) {
      setUserJourney([...userJourney, nextStep])
      setCurrentStep(nextStep)
    }
  }

  const handleBookmarkResources = async (
    operation: ToggleSaveOrClearOperation,
    resourceId?: string
  ) => {
    switch (operation) {
      case 'toggle': {
        if (!resourceId) return

        // Filter out the resource if it's already bookmarked
        const foundResourceIndex = resourcesToBookmark.indexOf(resourceId)
        if (foundResourceIndex > -1) {
          const updatedIds = resourcesToBookmark.filter(
            (id) => id !== resourceId
          )
          setResourcesToBookmark(updatedIds)
        } else {
          // Otherwise add the resource to the list of resources to bookmark
          setResourcesToBookmark([...resourcesToBookmark, resourceId])
        }
        break
      }

      // Save all resources to bookmark
      case 'save':
        if (resourcesToBookmark.length) {
          const bookmarkPromises = resourcesToBookmark.map(
            (academyResourceId) =>
              saveAcademyResourceForUserMutation({
                variables: { academyResourceId, userId },
              })
          )

          await Promise.all(bookmarkPromises)
        }
        break
      default:
        break
    }
  }

  const handleSelectTemplateGoals = async (
    operation: ToggleSaveOrClearOperation,
    templateLibraryId?: number
  ) => {
    switch (operation) {
      case 'toggle': {
        if (!templateLibraryId) return

        // Filter out the resource if it's already bookmarked
        const foundTemplateGoal =
          templateGoalsToCreate.indexOf(templateLibraryId)
        if (foundTemplateGoal > -1) {
          const updatedIds = templateGoalsToCreate.filter(
            (id) => id !== templateLibraryId
          )
          setTemplateGoalsToCreate(updatedIds)
        } else {
          // Otherwise add the resource to the list of resources to bookmark
          setTemplateGoalsToCreate([
            ...templateGoalsToCreate,
            templateLibraryId,
          ])
        }
        break
      }

      // Save all resources to bookmark
      case 'save':
        if (templateGoalsToCreate.length) {
          await createTemplateGoals({
            variables: { nonprofitOrgId, templateIds: templateGoalsToCreate },
          })
          popToast({
            content: 'Your goals have been successfully created!',
          })
        }
        break
      case 'clear':
        setTemplateGoalsToCreate([])
        break
      default:
        break
    }
  }

  const handleInviteOrgLeader = () => {
    const emailToInvite = orgLeaderInvite.email.trim()
    const invitedEmails = orgLeaderInvite.emailsInvited

    // Prevent duplicate email invite calls in case user goes back at some point
    if (orgLeaderInvite.emailsInvited.includes(emailToInvite)) return

    createUsers({
      variables: {
        nonprofitUserInfo: {
          emails: [emailToInvite],
          role: FunderOrgToUserRole.Admin,
        },
        nonprofitOrgId,
        sendInvite: true,
        inviteType: NonprofitOrgInviteType.TeamMember,
      },
    })

    popToast({
      content: 'Your Organization Leader has been invited!',
    })

    setOrgLeaderInvite({
      ...orgLeaderInvite,
      emailsInvited: [...invitedEmails, emailToInvite],
    })
  }

  const handleBack = () => {
    if (userJourney.length >= 2) {
      const previousStep = userJourney[userJourney.length - 2]
      setUserJourney(userJourney.slice(0, -1))
      setCurrentStep(previousStep)
    }
  }

  const userJourneyLength = userJourney.length

  const wizard = createWizardConfig({
    baselineConfidence,
    baselineSupportNeeded,
    nonprofitOrgId,
    onAnyAction: handleAnyAction,
    onBookmarkResources: handleBookmarkResources,
    onToggleTemplateGoal: handleToggleGoals,
    onFetchRecommendedResources: fetchAcademyResources,
    onInviteOrgLeader: handleInviteOrgLeader,
    onSetBaselineConfidence: setBaselineConfidence,
    onSetBaselineSupportNeeded: setBaselineSupportNeeded,
    onSetOrgLeaderInvite: setOrgLeaderInvite,
    onSetPriorities: setPriorities,
    onSetRecommendedResources: setRecommendedResources,
    onSetUserPrimaryFunction: setUserPrimaryFunction,
    onSetUserRole: setUserRole,
    onSelectTemplateGoals: handleSelectTemplateGoals,
    orgLeaderInvite,
    priorities,
    recommendedResources,
    resourcesToBookmark,
    templateGoalsToCreate,
    templateLibraryMap,
    userJourneyLength,
    userPrimaryFunction,
    userRole,
  })

  const step = wizard[currentStep]

  // This should never happen as the config steps are hard-coded, typed, and should always be present
  if (!step) return <>Oops! Please contact customer support.</>

  return (
    <FullWindowContainer>
      <Header>
        <ResiliaIconWhite />
        <SignOut onClick={() => logout()}>Sign Out</SignOut>
      </Header>
      <SurveyContainer>
        {loading ? (
          <Loading height='300px' />
        ) : (
          <WizardContainer>
            <WizardSteps>
              {step.showCloseIcon && <Close onClick={() => redirectHome()} />}
              {step.component}
            </WizardSteps>
            {!step.hideBackButton && (
              <BackButton>
                <Button label='Back' onClick={handleBack} variant='tertiary' />
              </BackButton>
            )}
          </WizardContainer>
        )}
      </SurveyContainer>
    </FullWindowContainer>
  )
}
